import * as R from 'ramda';
import { FILTER_KEYS } from '@/utils/consts';
import { getChannelOptionGroupId } from '@/utils/dashboard';
import { DEFAULT_OPERATOR_MAP } from '@/components/Dashboard/common/SearchBar/filters/const';

export const FILTER_WIDGET_LABEL_SUBTYPE = 'label';
export const FILTER_WIDGET_FIELD_VALUE_SUBTYPE = 'fieldValue';
const { TITLE, STATUS, CHANNEL, ID, AD_ACCOUNT_ID } = FILTER_KEYS;

const DROPDOWN_OPTION_KEYS = [
    'linkedWidgetIds',
    'allowMultiSelect',
    'sortAlphabetically',
    'availableLabels',
    'subType',
    'groupId',
    'by',
    'condition',
];


const FIELD_VALUE_ONLY_KEYS = ['groupId', 'by', 'condition'];
const LABEL_ONLY_KEYS = ['availableLabels'];

const pickDropdownOptionKeys = R.pick(DROPDOWN_OPTION_KEYS);

function hasFieldValueConfig(options = {}) {
    return (
        !R.isNil(options.groupId) ||
        !R.isNil(options.by) ||
        !R.isNil(options.condition)
    );
}

function hasLabelConfig(options = {}) {
    return !R.isNil(options.availableLabels);
}

export function isFieldValueSubType(widgetOrSubType) {
    const subType =
        typeof widgetOrSubType === 'string'
            ? widgetOrSubType
            : widgetOrSubType?.subType;

    return subType === FILTER_WIDGET_FIELD_VALUE_SUBTYPE;
}

export function isLabelBy(by) {
    return by?.type === 'label';
}

export function sanitizeBy(by) {
    if (!by || typeof by !== 'object') {
        return undefined;
    }

    if (by.type === 'label') {
        return { type: 'label' };
    }

    if (by.type === 'field' && by.field) {
        return R.pickBy(R.complement(R.isNil), {
            type: 'field',
            field: by.field,
            level: by.level,
        });
    }

    return undefined;
}

export function sanitizeCondition(condition) {
    if (!condition || typeof condition !== 'object') {
        return undefined;
    }

    const refinedField =
        condition.dataSourceType || condition.type || condition.field;

    if (!refinedField || !condition.operator) {
        return undefined;
    }

    if (!Array.isArray(condition.values)) {
        return { ...condition };
    }

    const values = condition.values.filter(
        (value) => !R.isNil(value) && String(value).trim() !== ''
    );

    if (!values.length) {
        return undefined;
    }

    return {
        ...condition,
        values,
    };
}

function stripKeys(widget, keys) {
    return R.omit(keys, widget || {});
}

function makeLabelDraftState(options = {}) {
    return {
        ...stripKeys(options, FIELD_VALUE_ONLY_KEYS),
        subType: FILTER_WIDGET_LABEL_SUBTYPE,
        availableLabels: options.availableLabels ?? null,
        groupId: undefined,
        by: undefined,
        condition: undefined,
    };
}

function makeFieldValueDraftState(options = {}) {
    return {
        ...stripKeys(options, LABEL_ONLY_KEYS),
        subType: FILTER_WIDGET_FIELD_VALUE_SUBTYPE,
        availableLabels: null,
        groupId: options.groupId,
        by: sanitizeBy(options.by),
        condition: sanitizeCondition(options.condition),
    };
}

export function normalizeWidgetDropdownConfig(widget = {}) {
    const options = pickDropdownOptionKeys(widget);
    const explicitSubType = options.subType;

    if (explicitSubType === FILTER_WIDGET_FIELD_VALUE_SUBTYPE) {
        return makeFieldValueDraftState(options);
    }

    if (explicitSubType === FILTER_WIDGET_LABEL_SUBTYPE) {
        return makeLabelDraftState(options);
    }

    if (hasFieldValueConfig(options)) {
        return makeFieldValueDraftState(options);
    }

    if (hasLabelConfig(options)) {
        return makeLabelDraftState(options);
    }

    return makeLabelDraftState(options);
}

export function serializeWidgetDropdownConfig(filterWidgetOptions = {}) {
    if (isFieldValueSubType(filterWidgetOptions)) {
        const sanitizedCondition = sanitizeCondition(
            filterWidgetOptions.condition
        );

        const serialized = {
            linkedWidgetIds: filterWidgetOptions.linkedWidgetIds,
            allowMultiSelect: filterWidgetOptions.allowMultiSelect,
            sortAlphabetically: filterWidgetOptions.sortAlphabetically,
            subType: FILTER_WIDGET_FIELD_VALUE_SUBTYPE,
            groupId: filterWidgetOptions.groupId,
            by: sanitizeBy(filterWidgetOptions.by),
            // Persist "no condition" explicitly so origin-view deep merges
            // do not resurrect a previously saved condition object.
            condition: sanitizedCondition ?? null,
        };

        return R.pickBy(
            (value, key) => key === 'condition' || !R.isNil(value),
            serialized
        );
    }

    return R.pickBy(R.complement(R.isNil), {
        linkedWidgetIds: filterWidgetOptions.linkedWidgetIds,
        allowMultiSelect: filterWidgetOptions.allowMultiSelect,
        sortAlphabetically: filterWidgetOptions.sortAlphabetically,
        subType: FILTER_WIDGET_LABEL_SUBTYPE,
        availableLabels: filterWidgetOptions.availableLabels,
    });
}

export function stripLabelModeFields(widget = {}) {
    return stripKeys(widget, FIELD_VALUE_ONLY_KEYS);
}

export function stripFieldValueModeFields(widget = {}) {
    return stripKeys(widget, [...FIELD_VALUE_ONLY_KEYS, ...LABEL_ONLY_KEYS]);
}

export function getChannelOptions(
    tableAdAccounts = [],
    labelGetter = R.identity
) {
    const groupIds = R.compose(
        R.uniq,
        R.filter((groupId) => !R.isNil(groupId) && groupId !== ''),
        R.map(getChannelOptionGroupId)
    )(tableAdAccounts || []);

    const priorityByGroupId = {
        marketing: 0,
        organic: 1,
    };

    return R.sortWith(
        [
            R.ascend((groupId) => priorityByGroupId[groupId] ?? 99),
            R.ascend((groupId) =>
                String(labelGetter(groupId) || groupId).toLowerCase()
            ),
        ],
        groupIds
    );
}

export function getFilterGroupKind(groupId, tableAdAccountsByGroupId = {}) {
    if (!groupId) {
        return undefined;
    }

    if (groupId === 'marketing') {
        return 'marketing';
    }

    if (groupId === 'organic') {
        return 'organic';
    }

    const scopedAccounts = R.propOr([], groupId, tableAdAccountsByGroupId);
    const primaryAccount = R.head(scopedAccounts) ?? {};

    return primaryAccount?.isFreeFormat ? 'genericSource' : 'genericChannel';
}

export function getConditionFieldOptions(by) {
    if (!by) {
        return [];
    }

    if (by.type !== 'field') {
        return [];
    }

    if (by.field === TITLE && by.level === 'adAccount') {
        return [{ field: TITLE, level: 'adAccount' }];
    }

    if (
        (by.field === TITLE || by.field === ID) &&
        by.level === 'campaign'
    ) {
        return [
            { field: TITLE, level: 'campaign' },
            { field: ID, level: 'campaign' },
            { field: STATUS, level: 'campaign' },
        ];
    }

    if (by.field === CHANNEL) {
        return [{ field: CHANNEL }];
    }

    if (by.field === AD_ACCOUNT_ID) {
        return [{ field: AD_ACCOUNT_ID }];
    }

    return [];
}

export function makeDefaultCondition(by) {
    const conditionFieldOptions = getConditionFieldOptions(by);
    const matchedOption = conditionFieldOptions.find(
        (option) => option.field === by?.field && option.level === by?.level
    );
    const firstOption = matchedOption || R.head(conditionFieldOptions);

    if (!firstOption) {
        return undefined;
    }

    return {
        field: firstOption.field,
        level: firstOption.level,
        operator: DEFAULT_OPERATOR_MAP[firstOption.field],
        values: undefined,
        not: false,
    };
}
